import React, { useEffect, useState, useContext } from 'react';
import { Grid, Button, Container } from '@material-ui/core';
import i18n from 'i18n-js';
import Title from 'components/Title';
import Layout from 'components/Layout';
import Comments from 'components/Comments';
import { useDispatch, useSelector } from 'react-redux';
import { vehicleActions } from 'state/modules/vehicles';
import {
  ChangeVehicleStatusModal,
  ChangeBrokersModal,
  ChangeServicesModal,
} from 'components/Modals';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';

import {
  VehicleDetailsWidget,
  Telemetry,
  Services,
  VehicleLogs,
  AssignedGroupsWidget,
  Statuses,
} from 'components/Vehicles';
import Can from 'components/Can';
import Brokers from 'components/Brokers';
import useStyles from './Vehicle.styled';
import Feature from 'components/Feature';
import { AlarmsWidget } from 'components/Alarms';
import OrgAbility from 'components/OrgAbility';
import { withRouter, useParams } from 'react-router-dom';
import { UserContext } from 'context';
import DocumentsWidget from 'components/Documents/Widget';
import { VehicleImages } from 'components/Vehicles';
import { ReservationsWidget } from 'components/Reservations';

const VehicleDetails = ({ history, width }) => {
  const { id } = useParams();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [openChangeServicesModal, setOpenChangeServicesModal] = useState(false);
  const [openChangeBrokersModal, setOpenChangeBrokersModal] = useState(false);

  const vehicle = useSelector((state) => state.vehicles.vehicle);
  const orgs = useSelector((state) => state.vehicles.orgs);
  const fetchedChangeBrokersSuccess = useSelector(
    (state) => state.vehicles.fetchedChangeBrokersSuccess
  );
  const fetchedChangeServicesSuccess = useSelector(
    (state) => state.vehicles.fetchedChangeServicesSuccess
  );
  const steps = useSelector((state) => state.vehicles.onboarding.steps);

  const { orgType } = useContext(UserContext);

  const handleStatusModalOpen = () => {
    dispatch(vehicleActions.resetChangeStatus());
    setOpenStatusModal((prevState) => !prevState);
  };

  const handleServicesModalOpen = () => {
    setOpenChangeServicesModal((prevState) => !prevState);
  };

  const handleBrokersModalOpen = () => {
    setOpenChangeBrokersModal((prevState) => !prevState);
  };

  useEffect(() => {
    if (id) {
      dispatch(vehicleActions.fetchVehicleById(id));
      dispatch(vehicleActions.fetchVehicleOrgsById(id));
    }
    dispatch(vehicleActions.resetOnboarding());
  }, []); // eslint-disable-line

  useEffect(() => {
    if (fetchedChangeBrokersSuccess || fetchedChangeServicesSuccess) {
      dispatch(vehicleActions.fetchVehicleById(id));
      dispatch(vehicleActions.fetchVehicleOrgsById(id));
    }
  }, [id, dispatch, fetchedChangeBrokersSuccess, fetchedChangeServicesSuccess]);

  /**
   * Broker menu list
   * It has the label and the function for each action
   */
  let brokerItems = [];
  let servicesItems = [];

  if (isWidthUp('sm', width)) {
    brokerItems =
      orgType === 'msOwnerOrg'
        ? [
            {
              title: i18n.t('vehicle.edit_broker'),
              onClick: handleBrokersModalOpen,
              can: { action: 'update', name: 'vehicle' },
            },
          ]
        : [];
    servicesItems = [
      {
        title: i18n.t('vehicle.edit_services'),
        onClick: handleServicesModalOpen,
        can: { action: 'update', name: 'vehicle' },
      },
    ];
  }

  return (
    <Layout title={i18n.t('vehicle.details_title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" justifyContent="space-between">
              <Grid item>
                <Title title={i18n.t('vehicle.details_title')} />
              </Grid>
              <Grid item>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleStatusModalOpen}
                  style={{ textTransform: 'inherit' }}
                  data-cy="change-status"
                >
                  {i18n.t('vehicle.change_status')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item sm={12} md={6} lg={4}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <VehicleDetailsWidget id={id} vehicle={vehicle} />
                  </Grid>
                  <Grid item xs={12}>
                    <VehicleImages id={vehicle?.id} />
                  </Grid>
                  <Grid item xs={12}>
                    <Statuses id={parseInt(id)} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item sm={12} md={6} lg={4}>
                <Grid container spacing={2}>
                  <Feature name="telemetry" is="enabled">
                    <Grid item xs={12}>
                      <Telemetry vehicleId={id} timezone={vehicle?.vehicle_group?.time_zone} />
                    </Grid>
                  </Feature>
                  <Can I="read" a="document">
                    <Grid item xs={12}>
                      <DocumentsWidget type="Vehicle" id={parseInt(id)} />
                    </Grid>
                  </Can>
                  <OrgAbility subject={['msOwnerOrg']}>
                    <Grid item xs={12} sm={12}>
                      <Services id={id} services={orgs} menuOptions={servicesItems} />
                    </Grid>
                  </OrgAbility>
                  <Can I="read" on="vehicle">
                    <Grid item xs={12} sm={12}>
                      <Brokers
                        id={id}
                        type="vehicle"
                        menuOptions={brokerItems}
                        brokers={vehicle?.brokers || {}}
                      />
                    </Grid>
                  </Can>
                  <Grid item xs={12}>
                    <AssignedGroupsWidget
                      vehicle_id={vehicle.id}
                      vehicle_group={
                        vehicle.vehicle_group ||
                        vehicle.customer_vehicle_group ||
                        vehicle.owner_vehicle_group ||
                        {}
                      }
                      params={{
                        parent_id:
                          vehicle?.vehicle_group?.parent?.id ||
                          vehicle?.customer_vehicle_group?.parent?.id ||
                          vehicle?.owner_vehicle_group?.parent?.id,
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item sm={12} md={6} lg={4}>
                <Grid container spacing={2}>
                  <Feature name="reservation" is="enabled">
                    <Grid item xs={12} sm={12}>
                      <ReservationsWidget
                        type="vehicle"
                        history={history}
                        customParams={{
                          vehicle_id: id,
                          statuses: ['RESERVED', 'INPROGRESS', 'OFFLINE', 'COMPLETED', 'NOSHOW'],
                        }}
                      />
                    </Grid>
                  </Feature>
                  <Can I="read" a="conditional_instance">
                    <Grid item xs={12}>
                      <AlarmsWidget type="vehicle" id={parseInt(id)} />
                    </Grid>
                  </Can>
                  <Can I="read" on="event_log">
                    <Grid item xs={12}>
                      <VehicleLogs id={id} />
                    </Grid>
                  </Can>
                  <Grid item xs={12}>
                    <Comments type="vehicle" id={parseInt(id)} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
      {openStatusModal && (
        <ChangeVehicleStatusModal
          open={true}
          vehicleId={parseInt(id)}
          vehicle={vehicle}
          handleClick={handleStatusModalOpen}
        />
      )}
      {openChangeServicesModal && (
        <ChangeServicesModal
          open={true}
          vehicle={vehicle}
          step={steps?.services}
          handleClick={handleServicesModalOpen}
          orgs={orgs}
        />
      )}
      {openChangeBrokersModal && (
        <ChangeBrokersModal
          open={true}
          source="vehicle"
          vehicle={vehicle}
          handleClick={handleBrokersModalOpen}
        />
      )}
    </Layout>
  );
};

export default withWidth()(withRouter(VehicleDetails));
